import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MarketingLayout, { Seo, Reveal, PageHero, FinalCta } from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Check, X as XIcon, ArrowRight } from "@phosphor-icons/react";

const SHOWCASE = [
  { key: "monthly", name: "AI Monthly Planner", img: "/shots/vozelto-marketing-calendar.jpg",
    benefit: "Generate an entire month of marketing in one click — 28-31 varied posts mixing promos, tips, holidays and stories, straight into your Approval Queue. Approve once and everything auto-schedules." },
  { key: "approvals", name: "Approval Queue", img: "/shots/vozelto-approval-queue.jpg",
    benefit: "Nothing goes live without you. Every post, email and ad waits for your sign-off — with comments, edits and one-click AI improvements." },
  { key: "readiness", name: "Campaign Readiness Score", img: "/shots/vozelto-approval-queue.jpg",
    benefit: "A 100-point agency-style review on every design — brand consistency, readability, CTA strength — so you publish with confidence, not guesswork." },
  { key: "image-studio", name: "AI Image Studio", img: "/shots/vozelto-image-studio.jpg",
    benefit: "Product photography and ad creative that looks like a $5k shoot — built with your real logo and products, scored by AI vision before you see it." },
  { key: "social", name: "AI Social Manager", img: "/shots/vozelto-social-manager.jpg",
    benefit: "Wake up to a week of on-brand posts, captions and hashtags — then publish straight to Facebook and Instagram from one place." },
  { key: "calendar", name: "Marketing Calendar", img: "/shots/vozelto-marketing-calendar.jpg",
    benefit: "Your whole month planned on one calendar. Drag, edit and reschedule anything — your marketing runs on schedule." },
  { key: "dashboard", name: "Executive Dashboard", img: "/shots/vozelto-executive-dashboard.jpg",
    benefit: "One screen tells you how the business is doing and what your AI team recommends next. Built for owners, not analysts." },
  { key: "analytics", name: "Analytics", img: "/shots/vozelto-analytics.jpg",
    benefit: "Traffic, revenue, ads and conversion in plain English — with the 'so what' included, not just charts." },
  { key: "brand-assets", name: "Brand Assets", img: "/shots/vozelto-brand-assets.jpg",
    benefit: "One brand library powers every AI employee. Your logo, voice and colors show up correctly, everywhere, automatically." },
  { key: "templates", name: "Templates", img: "/shots/vozelto-templates.jpg",
    benefit: "20+ proven campaign templates for every channel and industry — personalized to your brand and launched in one click." },
];

const CAPABILITIES = [
  { group: "Create", items: ["Monthly content generation (1-3 posts/day)", "Social posts with captions & hashtags", "Email & SMS campaigns", "Blog articles & SEO content", "AI product photography & ad creative", "Campaign templates (20+)"] },
  { group: "Control", items: ["Approval Queue on everything", "Campaign Readiness Score (100-pt)", "One-click AI improvements", "Brand Assets library", "Multi-workspace isolation", "Role-based team access"] },
  { group: "Publish & Measure", items: ["Direct Facebook & Instagram publishing", "Auto-scheduling onto Marketing Calendar", "Shopify product & order sync", "GA4 and ads analytics", "Plain-English AI reporting", "Morning executive briefings"] },
];

const COMPARISON = [
  ["Complete monthly content generation", true, false],
  ["12 coordinated AI specialists", true, false],
  ["Uses your real logo & brand voice", true, "partial"],
  ["Human approval before anything publishes", true, "partial"],
  ["Direct FB/IG publishing", true, true],
  ["Shopify + GA4 + ads in one view", true, false],
  ["One subscription, one login", true, false],
];

export default function FeaturesPage() {
  const navigate = useNavigate();
  const [showcase, setShowcase] = useState(SHOWCASE[0]);
  return (
    <MarketingLayout>
      <Seo title="Features — Vozelto AI Marketing Platform" path="/features"
        description="Explore Vozelto features: AI Monthly Planner, Approval Queue, AI Image Studio, direct Facebook & Instagram publishing, Shopify sync, analytics and more." />
      <PageHero caps="Platform features" title={<>Everything a marketing department does.<br /><span className="brand-text">In one platform.</span></>}
        subtitle="Vozelto creates, you approve, it publishes and measures. These are the real screens of the platform working on a live brand." />

      <section className="max-w-6xl mx-auto px-5 pb-16">
        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 space-y-1.5" data-testid="features-showcase-tabs">
            {SHOWCASE.map((f) => (
              <button key={f.key} data-testid={`features-tab-${f.key}`} onClick={() => setShowcase(f)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-200 ${showcase.key === f.key ? "border-[#6B3DFF]/35 bg-[#6B3DFF]/[0.07]" : "border-white/8 hover:border-white/20"}`}>
                <div className={`text-sm font-semibold ${showcase.key === f.key ? "text-white" : "text-[#9a9a9a]"}`}>{f.name}</div>
                {showcase.key === f.key && <div className="text-xs text-[#b5b5b5] mt-1 leading-relaxed">{f.benefit}</div>}
              </button>
            ))}
          </div>
          <div className="lg:col-span-3">
            <div className="card-matte brand-glow p-2 sticky top-24">
              <img key={showcase.key} src={showcase.img} alt={showcase.name} width="1600" height="900" loading="lazy"
                className="rounded-lg border border-white/5 w-full demo-fade" data-testid="features-showcase-image" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#0a0a0a]/60">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <Reveal className="max-w-2xl mb-10">
            <div className="label-caps mb-3">Full capability list</div>
            <h2 className="font-head font-bold text-3xl tracking-tight">Create. Control. Publish. Measure.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5" data-testid="capability-groups">
            {CAPABILITIES.map((c) => (
              <Reveal key={c.group}>
                <div className="card-matte p-6 h-full">
                  <div className="label-caps brand-text mb-4">{c.group}</div>
                  <ul className="space-y-2.5">
                    {c.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-sm text-[#cfcfcf]">
                        <Check size={15} className="brand-text mt-0.5 shrink-0" weight="bold" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 py-16">
        <Reveal className="text-center mb-10">
          <div className="label-caps mb-3">Feature comparison</div>
          <h2 className="font-head font-bold text-3xl tracking-tight">Vozelto vs. a stack of point tools</h2>
        </Reveal>
        <Reveal>
          <div className="card-matte overflow-hidden" data-testid="feature-comparison-table">
            <div className="grid grid-cols-3 text-xs font-semibold border-b border-white/8">
              <div className="px-4 py-3 text-[#9a9a9a]">Capability</div>
              <div className="px-4 py-3 text-center brand-text">Vozelto</div>
              <div className="px-4 py-3 text-center text-[#9a9a9a]">Separate tools</div>
            </div>
            {COMPARISON.map(([cap, us, them]) => (
              <div key={cap} className="grid grid-cols-3 items-center border-b border-white/5 last:border-0">
                <div className="px-4 py-3 text-sm text-[#cfcfcf]">{cap}</div>
                <div className="px-4 py-3 flex justify-center">{us === true ? <Check size={17} className="brand-text" weight="bold" /> : <span className="text-xs text-[#9a9a9a]">Partial</span>}</div>
                <div className="px-4 py-3 flex justify-center">{them === true ? <Check size={17} className="text-[#9a9a9a]" weight="bold" /> : them === "partial" ? <span className="text-xs text-[#7a7a7a]">Partial</span> : <XIcon size={15} className="text-red-400/60" weight="bold" />}</div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal className="mt-8 text-center">
          <Button data-testid="features-cta" onClick={() => navigate("/signup")} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-7 h-12">
            Try Every Feature Free for 14 Days <ArrowRight size={16} className="ml-1.5" weight="bold" />
          </Button>
        </Reveal>
      </section>
      <FinalCta />
    </MarketingLayout>
  );
}
